import idx from 'idx'
import _ from 'lodash'
import Decimal from 'decimal.js'
import { numberFormat } from './currency-formatter'
import { toDecimal } from './decimal-utils'

// Inline Item Constants
export const INLINE_ITEM_IDENTIFIER = 'INLINE_ITEM';
export const INLINE_CATALOG_DISPLAY_NAME = 'Inline Catalog';
export const INLINE_CATEGORY_DISPLAY_NAME = 'Inline Category';

/**
 * Check if an item is an inline item (non-catalog item)
 * @param {Object} item - The item object to check
 * @returns {boolean} - True if the item is an inline item
 */
export const isInlineItem = (item) => {
    if (!item) return false;
    return !!(
        item.isInlineItem ||
        item.catalog === INLINE_ITEM_IDENTIFIER ||
        item.categoryId === INLINE_ITEM_IDENTIFIER
    );
};

/**
 * Extracts materials from tasks and subtasks based on task detail.
 * Supports attribution level logic for stage-based material extraction.
 * 
 * @param {Array} tasks - Array of tasks with materials and subtasks
 * @param {Object} taskDetail - Selected task detail object
 * @param {string} taskDetail.value - Task or stage ID to search for
 * @param {boolean} taskDetail.isAttributionLevel - If true, searches by stage attribution
 * @param {string} taskDetail.key - Task name (fallback)
 * @param {string} taskDetail.label - Task label name
 * @param {Array} taskDetail.descendantStageIds - Stage IDs for nested attribution stages
 * @returns {Object} { status: boolean, data: Array, msg: string }
 */
export const getMaterialsList = (tasks, taskDetail) => {
    let _task = []
    Array.isArray(tasks) && tasks.map((task) => {
        _task.push(task)
        Array.isArray(task.subTasks) && task.subTasks.map((subTask) => {
            _task.push({
                ...subTask,
                taskId: task.id,              // Parent task's ID
                parentTaskName: task.name,    // Parent task's name for fallback
                parentStageId: task.stageId
            })
        })
    })
    const isAttributionLevel = taskDetail.isAttributionLevel
    const isSection = taskDetail.isSection || taskDetail.isStage
    const descendantStageIds = Array.isArray(taskDetail.descendantStageIds)
        ? new Set(taskDetail.descendantStageIds)
        : null;
    // Support for childTaskIds from section-dropdown-format.js
    const childTaskIds = Array.isArray(taskDetail.childTaskIds)
        ? new Set(taskDetail.childTaskIds)
        : null;

    let task = _task.filter(item => (
        item.id === taskDetail.value ||
        item.taskId === taskDetail.value ||
        // Check if task is in childTaskIds (section selection)
        (childTaskIds && childTaskIds.has(item.id)) ||
        ((isAttributionLevel || isSection)
            ? (
                item.stageId === taskDetail.value ||
                item.parentStageId === taskDetail.value ||
                (descendantStageIds && descendantStageIds.has(item.stageId)) ||
                (descendantStageIds && descendantStageIds.has(item.parentStageId))
            )
            : false)
    ))
    if (!task.length) return { status: false, msg: 'No Task found' }
    let items = []
    task.map(iterator => {
        Array.isArray(iterator.material) && iterator.material.forEach((item) => {
            let _item = typeof item === 'string' ? JSON.parse(item) : item
            items.push({
                ..._item,
                taskDescription: taskDetail.taskDescription,
                // Preserve original task data for Nearest Flagged Ancestor lookup
                // For subtasks: use parent task's ID and name (since dropdown shows tasks, not subtasks)
                originalTaskId: iterator.taskId || iterator.id,
                originalTaskName: iterator.parentTaskName || iterator.name,
                // originalStageId: iterator.stageId || iterator.parentStageId,
                originalStageId: iterator.parentStageId || iterator.stageId,
                // Set current task info from selection
                taskId: taskDetail.value,
                taskName: taskDetail.key || taskDetail.label,
                taskType: taskDetail.taskType
            })
        })
    })
    if (!items.length) return { status: false, msg: 'No Items found' }
    return { status: true, data: items }
}

export const sortVendorList = (vendors) => {
    if (!Array.isArray(vendors)) return []
    return _.orderBy(
        vendors,
        [v => v.vendor?.business_name?.toLowerCase()],
        ['asc']
    );

}

export const filterManufacturer = (items, filterValue) => {
    items = filterValidCatalog(items)
    const filters = makeFilter(items, filterValue);
    if (Object.keys(filters).length) {
        let validItems = items.map(item => { return { ...item, catalog: item?.category?.name || '' } })
        items = customFilter(validItems, filters)
    }
    items = filterUnique(items, 'manufacturer').map(item => { return { key: item, value: item, manufacturer: item } })
    return items;
}

export const filterItems = (items, filterValue) => {
    let _items = filterValidCatalog(items)
    const filters = makeFilter(_items, filterValue);
    if (Object.keys(filters).length) {
        let validItems = _items.map(item => { return { ...item, catalog: item?.category?.name || '' } })
        _items = customFilter(validItems, filters)
    }
    _items = _items.map(item => { return { key: `${item.item} | ${item.unit}`, value: item.id } })
    return _items;
}

const filterUnique = (items, prop) => {
    return [...new Set(items.map((item) => { return item[prop] }))]
}
const filterValidCatalog = (items) => {
    return items.map((item) => { if (item.categoryId) return item }).filter(item => item)
}
const makeFilter = (options, filterValue) => {
    let filters = {};
    const catalog = idx(filterValue, _ => _['catalog']) || '';
    const manufacturer = idx(filterValue, _ => _['manufacturer']) || '';
    const is_manufacturer = manufacturer ? options.some(item => ((item['manufacturer']) ? item['manufacturer'].toLowerCase() : '') === manufacturer.toLowerCase()) : '';
    const partNo = idx(filterValue, _ => _['partNo']) || '';
    const item = idx(filterValue, _ => _['item']) || '';
    const item_key = idx(options, _ => _[0]['item']) ? 'item' : 'key';

    if (catalog) filters['catalog'] = { 'includes': true, 'value': catalog, 'starts': false }
    if (manufacturer) filters['manufacturer'] = { 'includes': !is_manufacturer, 'value': manufacturer, 'starts': false }
    if (partNo) filters['partNo'] = { 'includes': false, 'value': partNo, 'starts': true }
    if (item) filters[item_key] = { 'includes': true, 'value': item, 'starts': false }

    return filters
}
const customFilter = (options, filters) => {
    for (let filter in filters) {
        options = options.filter((item) => {
            if (item[filter]) {
                if (filters[filter].includes) {
                    if (item[filter].toLowerCase().includes(filters[filter].value.toLowerCase())) return item;
                } else if (filters[filter].starts) {
                    if (item[filter].toLowerCase().startsWith(filters[filter].value.toLowerCase())) return item;
                } else {
                    if (item[filter].toLowerCase() === filters[filter].value.toLowerCase()) return item;
                }
            }
        })
    }
    return options;
}
const pattern = {
    sqlInjection: {
        key: /^[^%?*=]+$/,
        message: 'Invalid characters: %?*= are not allowed'
    },
}
export const sqlInjection = pattern.sqlInjection;

export const debounceTime = {
    inventoryList: 600,
    columnSearch: 100
}

export const arrayToObject = (array, keyField, { lowerCase = false }) =>
    array.reduce((obj, item) => {
        let key = item[keyField] || '';
        obj[lowerCase ? (key).toLowerCase() : key] = item
        return obj
    }, {});

export const extractLastStageData = ({ project = {}, stages = [] }) => {
    const lastStageData = [];

    const traverseStages = (stages) => {
        if (stages) {
            for (const stage of stages) {
                if (stage.stages) traverseStages(stage.stages);
                else lastStageData.push(stage);
            }
        }
    };

    if (project && project.stages) traverseStages(project.stages);
    if (stages) traverseStages(stages);

    return lastStageData;
};


export const getwebsite = (website) => {
    if (!website) return [];
    return website.split(',').map(item => item.trim());
};


export const getVariationNumber = (optionList = []) => {
    if (!Array.isArray(optionList)) return [];
    const options = _.cloneDeep(optionList);
    options.forEach((option) => {
        option.revisionNumber = idx(option, _ => _.taskRevision[0].RevisionNumber) || '';
        option.revisionId = idx(option, _ => _.taskRevision[0].RevisionId) || '';
        option.revisionName = idx(option, _ => _.taskRevision[0].revisionName) || '';
    });
    let sno = 0;
    const sortedtempArray = _.chain(options)
        .groupBy("revisionId")
        .map((revisions, revisionId) => {
            const sortedRevisions = _.sortBy(revisions, [(o) => new Date(o.createdAt || null)]) || [];
            return { revisionId, revisions: sortedRevisions };
        })
        .sortBy((o) => o.revisions[0].createdAt)
        .map((data) => data.revisions)
        .value()
        .flat(1);

    const uniqueIdArr = _.uniqBy(sortedtempArray, "revisionId");
    sortedtempArray.forEach((option, optionIndex) => {
        let index = _.findIndex(uniqueIdArr, function (item) {
            return item.id === option.id;
        });
        if (index !== -1) {
            option.revision = true;
            option.selected = true;
            sno += 1;
        } else {
            option.selected = false;
        }

        // if (option.proposal_selected && option.proposal_status === "APPROVED") {
        option.variationNo = generateVariationNumber(optionIndex + 1, 3);
        // }

        option.sno = sno;
    });

    return sortedtempArray;
}

export const generateVariationNumber = (n, width, z) => {
    z = z || "0";
    n = n + "";
    return n.length >= width ? `V-${n}` : `V-${new Array(width - n.length + 1).join(z) + n}`;
};


export const combineData = ({ project = [], stages = [], variation = [] }) => {
    let sectionData = [];
    if (!_.isEmpty(project) && !_.isEmpty(stages)) return sectionData;
    if (!_.isEmpty(project) && project.stages) sectionData = _.cloneDeep(idx(project, _ => _.stages) || []);
    if (!_.isEmpty(stages)) sectionData = _.cloneDeep(idx(stages, _ => _) || []);

    const variations = idx(variation, _ => _) || [];

    const traverseStages = (stages) => {
        if (!stages) return null;

        return stages.map(stage => {
            let updatedStage = { ...stage }; // Clone the current stage

            if (updatedStage.stages && Array.isArray(updatedStage.stages)) {
                // Recursively traverse sub-stages
                updatedStage.stages = traverseStages(updatedStage.stages);
            } else {
                // Process the stage and add variations
                const matchingVariations = variations.filter(variation => variation.stageId === updatedStage.id && !['REJECTED', 'CANCELLED'].includes(variation.proposal_status));
                if (matchingVariations.length > 0) {
                    if (!updatedStage.tasks) {
                        updatedStage.tasks = [];
                    }
                    updatedStage.tasks.push(...matchingVariations);
                }
            }
            return updatedStage;
        });
    };

    const updatedSectionData = traverseStages(sectionData);
    return updatedSectionData;
};

export const filterPartNo = (itemsList, filterValue) => {
    let filterOptions = [];
    if (Array.isArray(itemsList)) {
        const uniquePartNos = new Set();

        itemsList.forEach(item => {
            if (item.partNo) {
                uniquePartNos.add(item.partNo);
            }
        });

        filterOptions = Array.from(uniquePartNos).map(partNo => ({
            key: partNo,
            value: partNo
        }));
    }
    return filterOptions;
}

// Utility function to convert time string (e.g., "40:30") to decimal hours
export const timeToHours = (timeString) => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(":").map(Number);
    return hours + (minutes / 60);
};

/**
 * Formats decimal hours into a concise duration string (e.g. "1H:15M" or "2H").
 * Uses Decimal.js for precise conversion and Moment.js for duration parsing.
 *
 * @param {number|string} decimalHours - The decimal hour value to format.
 * @returns {string} Formatted duration string (e.g., "1H:30M", "0H").
 */
export const formatDecimalTime = (decimalHours) => {
    if (decimalHours === null || decimalHours === undefined || decimalHours === '') return '0H';

    try {
        const dec = new Decimal(decimalHours);
        if (dec.isZero() || dec.isNaN()) return '0H';

        const isNegative = dec.isNegative();
        const absMinutes = dec.abs().times(60).round();

        const h = absMinutes.dividedToIntegerBy(60).toNumber();
        const m = absMinutes.mod(60).toNumber();

        const formatted = m === 0 ? `${h}H` : `${h}H:${m}M`;
        return isNegative ? `-${formatted}` : formatted;
    } catch (e) {
        return '0H';
    }
};

/**
 * Formats planned effort for a labor item, appending hours/minutes notation for personnel resources.
 *
 * @param {Object} labor - Labor field state object
 * @returns {string} Formatted effort string
 */
export const formatPlannedEffort = (labor) => {
    const isPersonnel = labor?.is_personnel?.value !== false && labor?.isPersonnel?.value !== false;
    const dec = toDecimal(isPersonnel ? labor?.qty?.value : 0);
    if (dec.isNaN() || dec.lte(0)) {
        return numberFormat(0, true);
    }
    const plannedEffort = dec.toNumber();
    const formattedVal = numberFormat(plannedEffort, true);
    return `${formattedVal} (${formatDecimalTime(plannedEffort)})`;
};

// Utility function to parse numeric values consistently
export const parseNumericValue = (value, defaultValue = 0) => {
    if (typeof value === 'string') {
        return (value.replace(/,/g, '')) || defaultValue;
    }
    return (value || defaultValue);
}

export const parseFloatValue = (value, defaultValue = 0) => {
    if (typeof value === 'string') {
        return parseFloat(value.replace(/,/g, '')) || defaultValue;
    }
    return parseFloat(value || defaultValue);
}

export function sortByKeyAsc(list) {
    const normalize = (str) =>
        str.trim().toLowerCase().replace(/^(the|a|an)\s+/i, '');
    return _.orderBy(list, [({ key }) => normalize(key)], ['asc']);
}

export const parseAmount = (val) => {
    if (!val) return 0;
    // Remove $ and commas, then parse as float
    return parseFloat(
        String(val).replace(/[$,]/g, "")
    ) || 0;
};

export const disableArrows = (e) => {
    if (e.keyCode == 38 || e.keyCode == 40) {
        e.preventDefault();
    }
}